import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Calendar, Users, Target, CheckCircle, 
  Rocket, Activity, BarChart3, Award, Shield,
  FileText, UserCheck, BookOpen
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import NiveauxDecisionModal from './NiveauxDecisionModal';

interface ComiteMember {
  id: string;
  name: string;
  role: string;
  department: string;
  responsibility: string;
  attendance: number;
  status: 'active' | 'inactive' | 'new';
  expertise: string[];
  lastMeeting: string;
}

interface AgendaItem {
  id: string;
  title: string;
  category: 'Strategic' | 'Operational' | 'Financial' | 'Technical' | 'Governance';
  priority: 'High' | 'Medium' | 'Low';
  duration: number;
  presenter: string;
  status: 'Scheduled' | 'In Progress' | 'Completed' | 'Deferred';
  description: string;
  decisions: string[];
  actionItems: string[];
  nextSteps: string[];
}

interface Decision {
  id: string;
  title: string;
  date: string;
  category: string;
  impact: 'High' | 'Medium' | 'Low';
  status: 'Approved' | 'Pending' | 'Rejected' | 'Under Review';
  description: string;
  rationale: string;
  responsible: string;
  deadline: string;
  progress: number;
}

interface RoadmapItem {
  id: string;
  title: string;
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  year: number;
  status: 'Planned' | 'In Progress' | 'Completed' | 'Delayed';
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  budget: number;
  owner: string;
  dependencies: string[];
  milestones: string[];
}

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  icon: any;
  color: string;
}

interface ComiteTIProps {
  isOpen: boolean;
  onClose: () => void;
}

const comiteMembers: ComiteMember[] = [
  {
    id: 'jp-leclerc',
    name: 'Jean-Pierre Leclerc',
    role: 'Directeur Service TI',
    department: 'Direction TI',
    responsibility: 'Président du Comité - Décisions stratégiques',
    attendance: 95,
    status: 'active',
    expertise: ['Stratégie TI', 'Gouvernance', 'Budget', 'Innovation'],
    lastMeeting: '2024-01-15'
  },
  {
    id: 'marie-dubois',
    name: 'Marie Dubois',
    role: 'CFO',
    department: 'Finance',
    responsibility: 'Approbation budgétaire et ROI',
    attendance: 88,
    status: 'active',
    expertise: ['Budget', 'ROI', 'Contrôle financier', 'Audit'],
    lastMeeting: '2024-01-15'
  },
  {
    id: 'pierre-martin',
    name: 'Pierre Martin',
    role: 'CTO',
    department: 'Technologie',
    responsibility: 'Architecture technique et innovation',
    attendance: 92,
    status: 'active',
    expertise: ['Architecture', 'Innovation', 'Sécurité', 'Cloud'],
    lastMeeting: '2024-01-15'
  },
  {
    id: 'sophie-tremblay',
    name: 'Sophie Tremblay',
    role: 'Directrice RH',
    department: 'Ressources Humaines',
    responsibility: 'Gestion des talents et formation',
    attendance: 85,
    status: 'active',
    expertise: ['RH', 'Formation', 'Gestion talents', 'Culture'],
    lastMeeting: '2024-01-10'
  },
  {
    id: 'marc-bernard',
    name: 'Marc Bernard',
    role: 'Directeur Opérations',
    department: 'Opérations',
    responsibility: 'Impact opérationnel et continuité',
    attendance: 90,
    status: 'active',
    expertise: ['Opérations', 'Processus', 'Continuité', 'Efficacité'],
    lastMeeting: '2024-01-15'
  }
];

const agendaItems: AgendaItem[] = [
  {
    id: 'budget-2024',
    title: 'Approbation Budget TI 2024',
    category: 'Financial',
    priority: 'High',
    duration: 30,
    presenter: 'Jean-Pierre Leclerc',
    status: 'Scheduled',
    description: 'Présentation et approbation du budget TI pour 2024 avec focus sur l\'innovation IA',
    decisions: ['Budget approuvé à $2.55M', 'Allocation 15% pour innovation', 'ROI cible 220%'],
    actionItems: ['Finaliser contrats fournisseurs', 'Lancer appels d\'offres Q1'],
    nextSteps: ['Validation direction générale', 'Communication équipes']
  },
  {
    id: 'security-audit',
    title: 'Résultats Audit Sécurité NIST',
    category: 'Technical',
    priority: 'High',
    duration: 25,
    presenter: 'Pierre Martin',
    status: 'In Progress',
    description: 'Présentation des résultats de l\'audit de sécurité et plan d\'amélioration',
    decisions: ['Score NIST: 87%', 'Plan d\'action 6 mois', 'Budget sécurité +$50K'],
    actionItems: ['Formation équipes cybersécurité', 'Mise à jour politiques'],
    nextSteps: ['Audit de suivi Q2', 'Certification ISO 27001']
  },
  {
    id: 'ai-strategy',
    title: 'Stratégie IA et Automatisation',
    category: 'Strategic',
    priority: 'High',
    duration: 40,
    presenter: 'Jean-Pierre Leclerc',
    status: 'Scheduled',
    description: 'Définition de la stratégie IA pour 2024-2025 et roadmap d\'implémentation',
    decisions: ['POC IA générative Q1', 'Formation équipes IA', 'Budget innovation $180K'],
    actionItems: ['Sélection fournisseurs IA', 'Formation équipes'],
    nextSteps: ['Lancement POC', 'Évaluation résultats']
  },
  {
    id: 'cloud-migration',
    title: 'Migration Cloud - État d\'Avancement',
    category: 'Operational',
    priority: 'Medium',
    duration: 20,
    presenter: 'Pierre Martin',
    status: 'Completed',
    description: 'Mise à jour sur la migration cloud et optimisation des coûts',
    decisions: ['Migration 75% complétée', 'Économies $75K réalisées', 'Timeline respectée'],
    actionItems: ['Finaliser migration Q2', 'Optimiser coûts cloud'],
    nextSteps: ['Audit performance', 'Formation équipes']
  }
];

const decisions: Decision[] = [
  {
    id: 'dec-001',
    title: 'Approbation Budget TI 2024',
    date: '2024-01-15',
    category: 'Financial',
    impact: 'High',
    status: 'Approved',
    description: 'Budget TI approuvé à $2.55M avec focus innovation',
    rationale: 'Nécessaire pour maintenir la compétitivité et l\'innovation',
    responsible: 'Jean-Pierre Leclerc',
    deadline: '2024-01-31',
    progress: 100
  },
  {
    id: 'dec-002',
    title: 'Investissement Sécurité NIST',
    date: '2024-01-15',
    category: 'Security',
    impact: 'High',
    status: 'Approved',
    description: 'Investissement $50K pour améliorer la posture de sécurité',
    rationale: 'Conformité réglementaire et protection des données',
    responsible: 'Pierre Martin',
    deadline: '2024-06-30',
    progress: 25
  },
  {
    id: 'dec-003',
    title: 'Formation Équipes IA',
    date: '2024-01-10',
    category: 'Training',
    impact: 'Medium',
    status: 'Under Review',
    description: 'Programme de formation IA pour 25 employés',
    rationale: 'Préparer l\'équipe aux technologies émergentes',
    responsible: 'Sophie Tremblay',
    deadline: '2024-03-31',
    progress: 15
  }
];

const roadmapItems: RoadmapItem[] = [
  {
    id: 'roadmap-001',
    title: 'Migration Cloud Complète',
    quarter: 'Q1',
    year: 2024,
    status: 'In Progress',
    priority: 'High',
    budget: 120000,
    owner: 'Pierre Martin',
    dependencies: ['Formation équipes', 'Tests sécurité'],
    milestones: ['Migration 50%', 'Tests performance', 'Go-live']
  },
  {
    id: 'roadmap-002',
    title: 'POC IA Générative',
    quarter: 'Q1',
    year: 2024,
    status: 'Planned',
    priority: 'Critical',
    budget: 80000,
    owner: 'Jean-Pierre Leclerc',
    dependencies: ['Sélection fournisseur', 'Formation équipes'],
    milestones: ['Sélection outil', 'POC développement', 'Évaluation ROI']
  },
  {
    id: 'roadmap-003',
    title: 'Certification ISO 27001',
    quarter: 'Q2',
    year: 2024,
    status: 'Planned',
    priority: 'High',
    budget: 60000,
    owner: 'Pierre Martin',
    dependencies: ['Audit NIST', 'Formation équipes'],
    milestones: ['Préparation audit', 'Audit externe', 'Certification']
  },
  {
    id: 'roadmap-004',
    title: 'Optimisation Budget TI',
    quarter: 'Q2',
    year: 2024,
    status: 'Planned',
    priority: 'Medium',
    budget: 0,
    owner: 'Marie Dubois',
    dependencies: ['Audit licences', 'Négociation contrats'],
    milestones: ['Audit complet', 'Négociations', 'Économies réalisées']
  }
];

const createSlides = (setIsNiveauxModalOpen: (open: boolean) => void): Slide[] => [
  {
    id: 'overview',
    title: 'Comité TI - Vue d\'Ensemble',
    subtitle: 'Gouvernance, rôles et objectifs du comité',
    icon: Users,
    color: '#0066FF',
    content: (
      <div className="space-y-6">
        {/* Header Comité */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Users className="w-8 h-8" />
            Comité TI - Gouvernance Stratégique
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">5</div>
              <div className="text-white/90">Membres</div>
              <div className="text-sm text-white/80">Direction & Opérations</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">Mensuel</div>
              <div className="text-white/90">Fréquence</div>
              <div className="text-sm text-white/80">2h par réunion</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">90%</div>
              <div className="text-white/90">Participation</div>
              <div className="text-sm text-white/80">Moyenne 2023</div>
            </div>
          </div>
        </div>

        {/* Raison d'Être du Comité */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-xl border-l-4 border-green-500">
          <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-green-600" />
            Raison d'Être du Comité TI
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Objectifs Stratégiques</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Alignement TI avec stratégie d'entreprise
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Optimisation des investissements TI
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Gestion des risques technologiques
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Innovation et transformation digitale
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Valeur Ajoutée</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-blue-500" />
                  Décisions éclairées et rapides
                </li>
                <li className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-blue-500" />
                  Visibilité transversale des enjeux
                </li>
                <li className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-blue-500" />
                  Responsabilisation et suivi
                </li>
                <li className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-blue-500" />
                  Communication et transparence
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Membres du Comité */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Membres du Comité TI
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {comiteMembers.map((member) => (
              <div key={member.id} className="bg-white p-4 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold ${
                    member.status === 'active' ? 'bg-green-500' : 
                    member.status === 'inactive' ? 'bg-gray-400' : 'bg-blue-500'
                  }`}>
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">{member.name}</h4>
                    <p className="text-sm text-gray-600">{member.role}</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Département:</span>
                    <span className="font-medium">{member.department}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Participation:</span>
                    <span className="font-medium text-green-600">{member.attendance}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dernière réunion:</span>
                    <span className="font-medium">{member.lastMeeting}</span>
                  </div>
                </div>

                <div className="mt-3">
                  <h5 className="text-xs font-medium text-gray-700 mb-1">Expertise:</h5>
                  <div className="flex flex-wrap gap-1">
                    {member.expertise.slice(0, 2).map((skill, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                        {skill}
                      </span>
                    ))}
                    {member.expertise.length > 2 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                        +{member.expertise.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'schedule',
    title: 'Cédule et Ordre du Jour',
    subtitle: 'Planning des réunions et agenda détaillé',
    icon: Calendar,
    color: '#27AE60',
    content: (
      <div className="space-y-6">
        {/* Planning des Réunions */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Calendar className="w-8 h-8" />
            Planning des Réunions 2024
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
              <div className="text-2xl font-bold">15 Jan</div>
              <div className="text-white/90">Q1 Planning</div>
              <div className="text-sm text-white/80">Budget & Stratégie</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
              <div className="text-2xl font-bold">12 Fév</div>
              <div className="text-white/90">Sécurité & Audit</div>
              <div className="text-sm text-white/80">NIST & Conformité</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
              <div className="text-2xl font-bold">15 Mar</div>
              <div className="text-white/90">Innovation IA</div>
              <div className="text-sm text-white/80">POC & Roadmap</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
              <div className="text-2xl font-bold">12 Avr</div>
              <div className="text-white/90">Q2 Review</div>
              <div className="text-sm text-white/80">Performance & ROI</div>
            </div>
          </div>
        </div>

        {/* Ordre du Jour Détaillé */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-green-600" />
            Ordre du Jour - Réunion du 15 Janvier 2024
          </h3>
          <div className="space-y-4">
            {agendaItems.map((item, index) => (
              <div key={item.id} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 text-xs rounded-full ${
                      item.priority === 'High' ? 'bg-red-100 text-red-800' :
                      item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {item.priority}
                    </span>
                    <div className="text-sm text-gray-500 mt-1">{item.duration} min</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-blue-600" />
                      Présentateur
                    </h5>
                    <p className="text-sm text-gray-600">{item.presenter}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-purple-600" />
                      Catégorie
                    </h5>
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                      {item.category}
                    </span>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-green-600" />
                      Statut
                    </h5>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      item.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      item.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      item.status === 'Scheduled' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                </div>

                {item.decisions.length > 0 && (
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Décisions Prises
                    </h5>
                    <ul className="space-y-1">
                      {item.decisions.map((decision, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          {decision}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {item.actionItems.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <Rocket className="w-4 h-4 text-orange-600" />
                      Actions à Suivre
                    </h5>
                    <ul className="space-y-1">
                      {item.actionItems.map((action, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'decisions',
    title: 'Décisions et Suivi',
    subtitle: 'Historique des décisions et suivi des actions',
    icon: CheckCircle,
    color: '#8E44AD',
    content: (
      <div className="space-y-6">
        {/* Métriques des Décisions */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <CheckCircle className="w-8 h-8" />
            Suivi des Décisions 2024
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold">12</div>
              <div className="text-white/90">Décisions Prises</div>
              <div className="text-sm text-white/80">Q1 2024</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-300">85%</div>
              <div className="text-white/90">Taux d'Approbation</div>
              <div className="text-sm text-white/80">+5% vs 2023</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-300">78%</div>
              <div className="text-white/90">Actions Complétées</div>
              <div className="text-sm text-white/80">Dans les délais</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-300">$2.1M</div>
              <div className="text-white/90">Budget Approuvé</div>
              <div className="text-sm text-white/80">Q1 2024</div>
            </div>
          </div>
        </div>

        {/* Décisions Détaillées */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-purple-600" />
            Décisions Récentes et Suivi
          </h3>
          <div className="space-y-4">
            {decisions.map((decision) => (
              <div key={decision.id} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">{decision.title}</h4>
                    <p className="text-sm text-gray-600">{decision.description}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 text-xs rounded-full ${
                      decision.status === 'Approved' ? 'bg-green-100 text-green-800' :
                      decision.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      decision.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {decision.status}
                    </span>
                    <div className="text-sm text-gray-500 mt-1">{decision.date}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">Justification</h5>
                    <p className="text-sm text-gray-600">{decision.rationale}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">Responsable</h5>
                    <p className="text-sm text-gray-600">{decision.responsible}</p>
                    <p className="text-sm text-gray-500">Échéance: {decision.deadline}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progression</span>
                    <span>{decision.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${decision.progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Analyse des Décisions */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Analyse des Décisions par Catégorie
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { category: 'Financial', decisions: 4, approved: 3, budget: 2100 },
                { category: 'Security', decisions: 3, approved: 3, budget: 150 },
                { category: 'Training', decisions: 2, approved: 1, budget: 80 },
                { category: 'Technical', decisions: 3, approved: 2, budget: 200 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="decisions" fill="#8E44AD" name="Décisions" />
                <Bar dataKey="approved" fill="#27AE60" name="Approuvées" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'roadmap',
    title: 'Roadmap et Gouvernance',
    subtitle: 'Planification stratégique et cadre de gouvernance',
    icon: Target,
    color: '#E74C3C',
    content: (
      <div className="space-y-6">
        {/* Roadmap Stratégique */}
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6 rounded-xl">
          <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
            <Target className="w-8 h-8" />
            Roadmap Stratégique TI 2024
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter, index) => (
              <div key={quarter} className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center">
                <div className="text-2xl font-bold">{quarter}</div>
                <div className="text-white/90">2024</div>
                <div className="text-sm text-white/80 mt-2">
                  {index === 0 && 'Migration & IA'}
                  {index === 1 && 'Sécurité & Cert'}
                  {index === 2 && 'Optimisation'}
                  {index === 3 && 'Innovation'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Projets Roadmap */}
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Rocket className="w-6 h-6 text-red-600" />
            Projets et Initiatives
          </h3>
          <div className="space-y-4">
            {roadmapItems.map((item) => (
              <div key={item.id} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">{item.title}</h4>
                    <p className="text-sm text-gray-600">{item.quarter} {item.year} • Budget: ${(item.budget / 1000).toFixed(0)}K</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 text-xs rounded-full ${
                      item.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                      item.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                      item.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {item.priority}
                    </span>
                    <div className="text-sm text-gray-500 mt-1">{item.status}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">Propriétaire</h5>
                    <p className="text-sm text-gray-600">{item.owner}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-2">Dépendances</h5>
                    <div className="flex flex-wrap gap-1">
                      {item.dependencies.map((dep, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {dep}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h5 className="font-semibold text-gray-800 mb-2">Jalons</h5>
                  <div className="space-y-1">
                    {item.milestones.map((milestone, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        {milestone}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gouvernance BZ Interne */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4 border-blue-500">
          <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            Gouvernance BZ Interne - Solutions d'Affaires & Service TI
          </h3>
          
          {/* Structure Organisationnelle */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Structure Organisationnelle BZ</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold">
                    SA
                  </div>
                  <div>
                    <h5 className="font-bold text-green-800">Solutions d'Affaires</h5>
                    <p className="text-sm text-green-600">Stratégie & Tactiques Client</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Définition stratégie TI client</li>
                  <li>• Roadmap tactique et business</li>
                  <li>• Relations client et satisfaction</li>
                  <li>• Décisions d'investissement</li>
                </ul>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">
                    ST
                  </div>
                  <div>
                    <h5 className="font-bold text-blue-800">Service TI</h5>
                    <p className="text-sm text-blue-600">Exécution & Opérations</p>
                  </div>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Implémentation technique</li>
                  <li>• Architecture et infrastructure</li>
                  <li>• Support et maintenance</li>
                  <li>• Livraison opérationnelle</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Matrice RACI BZ */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">Matrice RACI - Imputabilité BZ</h4>
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-800">Activité</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-800">Solutions d'Affaires</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-800">Service TI</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-800">Client</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800">Définition Stratégie TI</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-bold">R</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">C</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">I</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800">Architecture Technique</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">C</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-bold">R</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">I</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800">Communication Client</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-bold">R</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">C</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">A</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-800">Exécution Projet</td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">C</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-bold">R</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">I</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-3 text-xs text-gray-600">
                <span className="font-semibold">Légende:</span> 
                <span className="ml-2"><span className="px-1 py-0.5 bg-red-100 text-red-800 rounded text-xs font-bold">R</span> = Responsable</span>
                <span className="ml-2"><span className="px-1 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs">A</span> = Imputable</span>
                <span className="ml-2"><span className="px-1 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">C</span> = Consulté</span>
                <span className="ml-2"><span className="px-1 py-0.5 bg-green-100 text-green-800 rounded text-xs">I</span> = Informé</span>
              </div>
            </div>
          </div>

          {/* Exemples Concrets - 3 Niveaux */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-3">Exemples Concrets - 3 Niveaux de Décision</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              {/* Niveau Stratégique */}
              <div className="space-y-3">
                <h5 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  Stratégique (Vision)
                </h5>
                <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="font-medium text-purple-800 text-sm">Transformation Numérique</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <strong>Solutions d'Affaires:</strong> Définit la vision, budget global, ROI 3-5 ans<br/>
                    <strong>Service TI:</strong> Consulte sur la faisabilité technique globale
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                  <div className="font-medium text-purple-800 text-sm">Stratégie Cybersécurité</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <strong>Solutions d'Affaires:</strong> Priorise les risques business, investissements<br/>
                    <strong>Service TI:</strong> Évalue l'architecture sécurité globale
                  </div>
                </div>
              </div>

              {/* Niveau Tactique */}
              <div className="space-y-3">
                <h5 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  Tactique (Moyen/Plan)
                </h5>
                <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="font-medium text-green-800 text-sm">Migration Cloud - Plan</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <strong>Solutions d'Affaires:</strong> Définit le plan, timeline, phases, budget détaillé<br/>
                    <strong>Service TI:</strong> Consulte sur l'architecture et les étapes techniques
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="font-medium text-green-800 text-sm">Roadmap Innovation IA</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <strong>Solutions d'Affaires:</strong> Priorise les cas d'usage, plan de déploiement<br/>
                    <strong>Service TI:</strong> Évalue l'architecture et l'implémentation par phases
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="font-medium text-green-800 text-sm">Plan de Continuité</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <strong>Solutions d'Affaires:</strong> Définit les processus business, RTO/RPO<br/>
                    <strong>Service TI:</strong> Consulte sur les solutions techniques disponibles
                  </div>
                </div>
              </div>
              
              {/* Niveau Technique */}
              <div className="space-y-3">
                <h5 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  Technique (Exécution)
                </h5>
                <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                  <div className="font-medium text-blue-800 text-sm">Configuration Serveurs</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <strong>Service TI:</strong> Exécute la configuration technique, monitoring<br/>
                    <strong>Solutions d'Affaires:</strong> Valide l'impact business et la conformité
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                  <div className="font-medium text-blue-800 text-sm">Sécurisation NIST</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <strong>Service TI:</strong> Implémente les contrôles techniques, tests<br/>
                    <strong>Solutions d'Affaires:</strong> Communique la conformité au client
                  </div>
                </div>
                <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                  <div className="font-medium text-blue-800 text-sm">Déploiement Application</div>
                  <div className="text-xs text-gray-600 mt-1">
                    <strong>Service TI:</strong> Exécute le déploiement, tests, monitoring<br/>
                    <strong>Solutions d'Affaires:</strong> Valide la fonctionnalité business
                  </div>
                </div>
              </div>
            </div>
            
            {/* Légende des Niveaux */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h6 className="font-semibold text-gray-800 text-sm">Hiérarchie des Décisions :</h6>
                <button
                  onClick={() => setIsNiveauxModalOpen(true)}
                  className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Guide Complet
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span><strong>Stratégique:</strong> Vision long terme, budget global, orientation business</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span><strong>Tactique:</strong> Moyen/Plan détaillé, phases, timeline, budget par projet</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span><strong>Technique:</strong> Exécution, configuration, implémentation, tests</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cadre de Gouvernance Client */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-xl border-l-4 border-gray-500">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Shield className="w-6 h-6 text-gray-600" />
            Cadre de Gouvernance Client
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Rôles et Responsabilités</h4>
              <div className="space-y-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">Président (Directeur TI)</div>
                  <div className="text-sm text-gray-600">Décisions stratégiques, budget, roadmap</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">CFO</div>
                  <div className="text-sm text-gray-600">Approbation budgétaire, ROI, contrôle financier</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">CTO</div>
                  <div className="text-sm text-gray-600">Architecture, innovation, sécurité technique</div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Processus de Décision</h4>
              <div className="space-y-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">Présentation</div>
                  <div className="text-sm text-gray-600">Document préparé 48h avant</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">Délibération</div>
                  <div className="text-sm text-gray-600">Discussion ouverte, questions</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">Décision</div>
                  <div className="text-sm text-gray-600">Vote majoritaire, consensus</div>
                </div>
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <div className="font-medium text-gray-800">Suivi</div>
                  <div className="text-sm text-gray-600">Actions assignées, échéances</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
];

export default function ComiteTI({ isOpen, onClose }: ComiteTIProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isNiveauxModalOpen, setIsNiveauxModalOpen] = useState(false);
  
  const slides = createSlides(setIsNiveauxModalOpen);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />
          
          {/* Main Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div 
                className="p-6 text-white"
                style={{ backgroundColor: currentSlideData.color }}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <currentSlideData.icon className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">{currentSlideData.title}</h2>
                      {currentSlideData.subtitle && (
                        <p className="text-white/90">{currentSlideData.subtitle}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/10 rounded-full p-2 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Slide {currentSlide + 1} sur {slides.length}</span>
                    <span>{Math.round(((currentSlide + 1) / slides.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                      className="bg-white h-2 rounded-full"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                  >
                    {currentSlideData.content}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center p-6 border-t bg-gray-50">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Précédent
                </button>
                
                <div className="flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        index === currentSlide ? 'bg-bz-blue' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === slides.length - 1}
                  className="flex items-center gap-2 px-4 py-2 bg-bz-blue hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
      
      {/* Modal Guide des Niveaux */}
      <NiveauxDecisionModal 
        isOpen={isNiveauxModalOpen} 
        onClose={() => setIsNiveauxModalOpen(false)} 
      />
    </AnimatePresence>
  );
}
